import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Code as CodeIcon,
  Speed as SpeedIcon,
  Assessment as AssessmentIcon,
  Add as AddIcon,
  PlayArrow as PlayIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cloud as CloudIcon,
  Storage as StorageIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useProjectContext } from '../contexts/ProjectContext';
import { Link as RouterLink } from 'react-router-dom';

interface Script {
  id: string;
  name: string;
  description: string;
  script_type: string;
  status: string;
  created_at: string;
  project_name: string;
  jmx_file?: string;
}

interface TestExecution {
  id: string;
  execution_id: string;
  status: string;
  start_time: string;
  end_time?: string;
  total_scripts: number;
  completed_scripts: number;
  failed_scripts: number;
  overall_progress: number;
  test_name: string;
  project_name: string;
}

interface Report {
  id: string;
  name: string;
  description: string;
  report_type: string;
  report_format: string;
  project_id: string;
  created_by: string;
  generated_at: string;
  created_at: string;
  file_path?: string;
  shareable_link?: string;
  project_name?: string;
}

interface DashboardStats {
  totalScripts: number;
  totalTests: number;
  activeTests: number;
  completedTests: number;
  failedTests: number;
  totalExecutions: number;
}

export const DashboardPage: React.FC = () => {
  const { user, logout } = useAuth();
  const { selectedProject } = useProjectContext();
  const [scripts, setScripts] = useState<Script[]>([]);
  const [recentExecutions, setRecentExecutions] = useState<TestExecution[]>([]);
  const [recentReports, setRecentReports] = useState<Report[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalScripts: 0,
    totalTests: 0,
    activeTests: 0,
    completedTests: 0,
    failedTests: 0,
    totalExecutions: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const fetchDashboardData = async () => {
    // Prevent duplicate API calls in React Strict Mode
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const projectId = selectedProject?.id;

      // Fetch recent scripts with project filtering
      const scriptsResponse = await fetch(`/api/scripts/paginated?limit=5${projectId ? `&project_id=${projectId}` : ''}`, { headers });
      const scriptsData = await scriptsResponse.json();
      if (scriptsData.success) {
        setScripts(scriptsData.data.items || []);
      }

      // Fetch recent executions with project filtering
      const executionsResponse = await fetch(`/api/intelliload/multi-script/executions/paginated?limit=5${projectId ? `&project_id=${projectId}` : ''}`, { headers });
      const executionsData = await executionsResponse.json();
      if (executionsData.success) {
        setRecentExecutions(executionsData.data.items || []);
      }

      // Fetch recent reports with project filtering
      const reportsResponse = await fetch(`/api/reports/recent/paginated?limit=5${projectId ? `&project_id=${projectId}` : ''}`, { headers });
      const reportsData = await reportsResponse.json();
      if (reportsData.success) {
        setRecentReports(reportsData.data.items || []);
      }

      // Calculate stats
      const statsResponse = await fetch(`/api/dashboard/stats${projectId ? `?project_id=${projectId}` : ''}`, { headers });
      const statsData = await statsResponse.json();
      if (statsData.success) {
        setStats(statsData.data);
      }

    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedProject]);

  const handleLogout = () => {
    logout();
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'running':
      case 'completed':
        return 'success';
      case 'failed':
      case 'error':
        return 'error';
      case 'pending':
      case 'initializing':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'running':
        return <PlayIcon />;
      case 'completed':
        return <CheckCircleIcon />;
      case 'failed':
      case 'error':
        return <ErrorIcon />;
      case 'pending':
      case 'initializing':
        return <ScheduleIcon />;
      default:
        return <HistoryIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScriptTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      'har_to_jmx': 'HAR to JMX',
      'postman_to_jmx': 'Postman to JMX',
      'swagger_to_jmx': 'Swagger to JMX',
      'manual_script': 'Manual Script'
    };
    return typeMap[type] || type;
  };

  const handleDownloadJMX = (script: Script) => {
    if (!script.jmx_file) {
      alert('No JMX file available for download');
      return;
    }

    // Extract filename from the jmx_file path
    // jmx_file format: "uploads/jmx/filename.jmx" or "uploads\jmx\filename.jmx" (Windows)
    const pathParts = script.jmx_file.split(/[\/\\]/); // Split by both forward slash and backslash
    const filename = pathParts[pathParts.length - 1]; // Get the last part
    if (!filename) {
      alert('Invalid JMX file path');
      return;
    }

    const token = localStorage.getItem('token');
    const downloadUrl = `/api/download/jmx/${filename}`;

    fetch(downloadUrl, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Download failed');
      }
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${script.name}.jmx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    })
    .catch(error => {
      console.error('Download error:', error);
      alert('Failed to download JMX file');
    });
  };

  const handleDownloadReport = (report: Report) => {
    const token = localStorage.getItem('token');
    const downloadUrl = `/api/intellireport/reports/${report.id}/download/excel`;

    fetch(downloadUrl, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Download failed');
      }
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    })
    .catch(error => {
      console.error('Download error:', error);
      alert('Failed to download report');
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', pl: 2, pr: 3, pt: 3, pb: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Welcome back, {user?.first_name || 'User'}! 👋
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {selectedProject ? `Working on: ${selectedProject.name}` : 'Select a project to get started'}
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Quick Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'primary.main' }}>
            <CardContent sx={{ color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.totalScripts}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Scripts
                  </Typography>
                </Box>
                <CodeIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'warning.main' }}>
            <CardContent sx={{ color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.activeTests}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active Tests
                  </Typography>
                </Box>
                <PlayIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'success.main' }}>
            <CardContent sx={{ color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.completedTests}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Completed Tests
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'info.main' }}>
            <CardContent sx={{ color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.totalExecutions}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Executions
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Recent Test Executions */}
        <Grid item xs={12} lg={4}>
          {/* Recent Test Executions */}
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Recent Test Executions"
              action={
                <Button
                  component={RouterLink}
                  to="/intelliload/multi-script"
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                >
                  View All
                </Button>
              }
            />
            <CardContent>
              {recentExecutions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No recent test executions
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/intelliload/multi-script"
                    variant="contained"
                    startIcon={<AddIcon />}
                  >
                    Start Your First Test
                  </Button>
                </Box>
              ) : (
                <List>
                  {recentExecutions.map((execution, index) => (
                    <React.Fragment key={execution.id}>
                      <ListItem>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: `${getStatusColor(execution.status)}.main` }}>
                            {getStatusIcon(execution.status)}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {execution.test_name || `Test Execution #${execution.execution_id.slice(0, 8)}`}
                              </Typography>
                              <Chip
                                label={execution.status}
                                color={getStatusColor(execution.status)}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {execution.project_name && `${execution.project_name} • `}Started: {formatDate(execution.start_time)}
                                {execution.end_time && ` • Ended: ${formatDate(execution.end_time)}`}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                <Typography variant="body2">
                                  Scripts: {execution.completed_scripts}/{execution.total_scripts}
                                </Typography>
                                {execution.status === 'running' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={execution.overall_progress}
                                      sx={{ flex: 1 }}
                                    />
                                    <Typography variant="body2" color="text.secondary">
                                      {execution.overall_progress}%
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          }
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              component={RouterLink}
                              to={`/intelliload/multi-script/execution/${execution.execution_id}`}
                              size="small"
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                      {index < recentExecutions.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Center Column - Recent Scripts */}
        <Grid item xs={12} lg={4}>
          {/* Recent Scripts */}
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Recent Scripts"
              action={
                <Button
                  component={RouterLink}
                  to="/scripts"
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                >
                  View All
                </Button>
              }
            />
            <CardContent>
              {scripts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CodeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No scripts created yet
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/intelliscript/ai-har-to-jmeter"
                    variant="contained"
                    startIcon={<AddIcon />}
                  >
                    Create Your First Script
                  </Button>
                </Box>
              ) : (
                <List>
                  {scripts.map((script, index) => (
                    <React.Fragment key={script.id}>
                      <ListItem>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <CodeIcon />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {script.name}
                              </Typography>
                              <Chip
                                label={getScriptTypeLabel(script.script_type)}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {script.description || 'No description'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Created: {formatDate(script.created_at)}
                                {script.project_name && ` • Project: ${script.project_name}`}
                              </Typography>
                            </Box>
                          }
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Download JMX">
                            <IconButton 
                              size="small"
                              onClick={() => handleDownloadJMX(script)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                      {index < scripts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Recent Reports Generated */}
        <Grid item xs={12} lg={4}>
          {/* Recent Reports Generated */}
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Recent Reports Generated"
              action={
                <Button
                  component={RouterLink}
                  to="/intellireport"
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                >
                  View All
                </Button>
              }
            />
            <CardContent>
              {recentReports.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <AssessmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No reports generated yet
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/intellireport/generate"
                    variant="contained"
                    startIcon={<AddIcon />}
                  >
                    Generate Your First Report
                  </Button>
                </Box>
              ) : (
                <List>
                  {recentReports.map((report, index) => (
                    <React.Fragment key={report.id}>
                      <ListItem>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: 'info.main' }}>
                            <AssessmentIcon />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {report.name}
                              </Typography>
                              <Chip
                                label={report.report_type}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {report.description || 'No description'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Generated: {formatDate(report.generated_at)}
                                {report.project_name && ` • Project: ${report.project_name}`}
                              </Typography>
                            </Box>
                          }
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Download Excel">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadReport(report)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                      {index < recentReports.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}; 